import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Chip, Container, Grid, Paper, Stack, Typography } from '@mui/material';
import { Helmet } from 'react-helmet-async';

import PageHeader from './PageHeader';
import StreamingDemo from '../StreamingDemo';
import CodeSample from '../CodeSample';
import { SITE_URL } from '../../lib/seo';
import { tArray } from '../../lib/i18nSafe';

export default function Playground() {
  const { t } = useTranslation();
  const tips = tArray(t, 'playgroundDemo.tips');

  return (
    <Box>
      <Helmet>
        <title>Playground | Al-Amen Technology</title>
        <link rel="canonical" href={`${SITE_URL}/playground`} />
      </Helmet>
      <PageHeader
        eyebrow={t('playground.eyebrow')}
        title={t('playground.title')}
        body={t('playground.body')}
      />

      <Container maxWidth="xl" sx={{ py: { xs: 5, md: 8 } }}>
        <Grid container spacing={{ xs: 4, md: 6 }}>
          <Grid item xs={12} md={7}>
            <StreamingDemo />
          </Grid>
          <Grid item xs={12} md={5}>
            <Paper
              variant="outlined"
              sx={{ p: { xs: 3, md: 3.5 }, borderRadius: 3, height: '100%', bgcolor: 'background.paper' }}
            >
              <Typography
                sx={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: '0.72rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.14em',
                  color: 'text.secondary',
                  mb: 1
                }}
              >
                {t('playground.tipsEyebrow')}
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'var(--font-serif)',
                  fontSize: '1.6rem',
                  letterSpacing: '-0.015em',
                  mb: 2
                }}
              >
                {t('playground.tipsTitle')}
              </Typography>
              <Stack spacing={1.5}>
                {tips.map((tip, idx) => (
                  <Stack key={tip} direction="row" spacing={1.5} alignItems="flex-start">
                    <Typography
                      sx={{
                        fontFamily: 'var(--font-mono)',
                        color: 'text.secondary',
                        fontSize: '0.82rem'
                      }}
                    >
                      0{idx + 1}
                    </Typography>
                    <Typography sx={{ color: 'text.secondary' }}>{tip}</Typography>
                  </Stack>
                ))}
              </Stack>
              <Stack direction="row" spacing={1} sx={{ mt: 3, flexWrap: 'wrap', gap: 1 }}>
                <Chip size="small" label={t('playground.meta.anon')} variant="outlined" />
                <Chip size="small" label={t('playground.meta.rateLimited')} variant="outlined" />
                <Chip size="small" label={t('playground.meta.noLogin')} variant="outlined" />
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      <Box
        component="section"
        sx={{
          py: { xs: 5, md: 8 },
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper'
        }}
      >
        <Container maxWidth="xl">
          <Grid container spacing={{ xs: 4, md: 6 }} alignItems="center">
            <Grid item xs={12} md={5}>
              <Typography
                sx={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: '0.72rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.14em',
                  color: 'text.secondary',
                  mb: 1.5
                }}
              >
                {t('playground.sdkEyebrow')}
              </Typography>
              <Typography
                component="h2"
                sx={{
                  fontFamily: 'var(--font-serif)',
                  fontSize: { xs: '2rem', md: '2.4rem' },
                  letterSpacing: '-0.02em',
                  lineHeight: 1.1,
                  mb: 2
                }}
              >
                {t('playground.sdkTitle')}
              </Typography>
              <Typography sx={{ color: 'text.secondary', maxWidth: 480 }}>
                {t('playground.sdkBody')}
              </Typography>
            </Grid>
            <Grid item xs={12} md={7}>
              <CodeSample />
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
}
